import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";

const ChartComponent = ({ type, data }) => {
  const chartOption = useMemo(() => {
    // Extract customer stats from the data
    const customerStats = data?.customer_stats || [];

    // Light theme colors
    const theme = {
      backgroundColor: "#ffffff",
      textStyle: { color: "#333333" },
      color: [
        "#5470c6",
        "#91cc75",
        "#fac858",
        "#ee6666",
        "#73c0de",
        "#3ba272",
        "#fc8452",
        "#9a60b4",
      ],
    };

    // Common axis label style
    const axisLabelStyle = {
      color: "#666666",
      fontWeight: "normal",
    };

    // Common title style
    const titleStyle = {
      color: "#333333",
      fontWeight: "bold",
    };

    switch (type) {
      case "bar":
        return {
          ...theme,
          title: {
            text: "Program Submissions",
            left: "center",
            textStyle: titleStyle,
          },
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "shadow" },
          },
          legend: {
            data: ["Total Submissions", "Valid Submissions"],
            textStyle: { color: "#666666" },
            bottom: 0,
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "10%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            data: customerStats.map((program) => program.program_title),
            axisLabel: {
              ...axisLabelStyle,
              rotate: 45,
              interval: 0,
            },
          },
          yAxis: {
            type: "value",
            name: "No. of Submissions",
            nameLocation: "end",
            nameGap: 30,
            nameTextStyle: {
              align: "left", // Align text left relative to its anchor
              padding: [0, 0, 0, -20], // [top, right, bottom, left] – pushes it right
            },
            axisLabel: axisLabelStyle,
          },
          series: [
            {
              name: "Total Submissions",
              type: "bar",
              data: customerStats.map((program) => program.total_submissions),
            },
            {
              name: "Valid Submissions",
              type: "bar",
              data: customerStats.map(
                (program) => program.total_valid_submissions
              ),
            },
          ],
        };

      case "pie":
        return {
          ...theme,
          title: {
            text: "Rewards Distribution",
            left: "center",
            textStyle: titleStyle,
          },
          tooltip: {
            trigger: "item",
            formatter: "{a} <br/>{b}: ${c} ({d}%)",
          },
          legend: {
            orient: "vertical",
            left: "left",
            textStyle: { color: "#666666" },
          },
          series: [
            {
              name: "Rewards",
              type: "pie",
              radius: "70%",
              center: ["50%", "50%"],
              data: customerStats.map((program) => ({
                value: program.highest_rewards,
                name: program.program_title,
              })),
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: "rgba(0, 0, 0, 0.2)",
                },
              },
              label: {
                formatter: "{b}: ${c}",
                color: "#333333",
              },
            },
          ],
        };

      case "donut":
        return {
          ...theme,
          title: {
            text: "Submission Validation Rate",
            left: "center",
            textStyle: titleStyle,
          },
          tooltip: {
            trigger: "item",
            formatter: "{a} <br/>{b}: {c} ({d}%)",
          },
          legend: {
            bottom: "1%",
            left: "center",
            textStyle: { color: "#666666" },
          },
          series: [
            {
              name: "Validation Rate",
              type: "pie",
              radius: ["25%", "50%"],
              avoidLabelOverlap: false,
              itemStyle: {
                borderRadius: 10,
                borderColor: "#fff",
                borderWidth: 2,
              },
              label: {
                show: false,
                position: "center",
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: "18",
                  fontWeight: "bold",
                  color: "#333333",
                },
              },
              labelLine: {
                show: false,
              },
              data: customerStats.map((program) => ({
                value: program.total_valid_submissions,
                name: program.program_title,
              })),
            },
          ],
        };

      case "line":
        return {
          ...theme,
          title: {
            text: "Submissions Trend",
            left: "center",
            textStyle: titleStyle,
          },
          tooltip: {
            trigger: "axis",
          },
          legend: {
            data: ["Total Submissions", "Valid Submissions"],
            textStyle: { color: "#666666" },
            bottom: 0,
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "10%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            boundaryGap: false,
            data: customerStats.map((program) => program.program_title),
            axisLabel: {
              ...axisLabelStyle,
              rotate: 45,
              interval: 0,
            },
          },
          yAxis: {
            type: "value",
            name: "Submissions Count",
            axisLabel: axisLabelStyle,
            nameTextStyle: {
              align: "left",
              padding: [0, 0, 0, -20],
            },
          },
          series: [
            {
              name: "Total Submissions",
              type: "line",
              data: customerStats.map((program) => program.total_submissions),
              smooth: true,
            },
            {
              name: "Valid Submissions",
              type: "line",
              data: customerStats.map(
                (program) => program.total_valid_submissions
              ),
              smooth: true,
            },
          ],
        };

      case "area":
        return {
          ...theme,
          title: {
            text: "Rewards Status",
            left: "center",
            textStyle: titleStyle,
          },
          tooltip: {
            trigger: "axis",
            axisPointer: {
              type: "cross",
              label: {
                backgroundColor: "#6a7985",
              },
            },
            formatter: function (params) {
              const program = params[0].name;
              const paidValue = params[0].value;
              const remainingValue = params[1].value;
              const total = paidValue + remainingValue;

              return `${program}<br/>
                     ${params[0].seriesName}: $${paidValue} (${Math.round(
                (paidValue / total) * 100
              )}%)<br/>
                     ${params[1].seriesName}: $${remainingValue} (${Math.round(
                (remainingValue / total) * 100
              )}%)<br/>
                     Total Budget: $${total}`;
            },
          },
          legend: {
            data: ["Rewards Paid", "Remaining Rewards"],
            textStyle: { color: "#666666" },
            bottom: 0,
          },
          grid: {
            left: "3%",
            right: "4%",
            bottom: "10%",
            containLabel: true,
          },
          xAxis: {
            type: "category",
            boundaryGap: false,
            data: customerStats.map((program) => program.program_title),
            axisLabel: {
              ...axisLabelStyle,
              rotate: 45,
              interval: 0,
            },
          },
          yAxis: {
            type: "value",
            name: "Amount (USD)",
            axisLabel: {
              ...axisLabelStyle,
              formatter: "${value}",
            },
          },
          series: [
            {
              name: "Rewards Paid",
              type: "line",
              stack: "Total",
              areaStyle: {},
              emphasis: {
                focus: "series",
              },
              data: customerStats.map(
                (program) =>
                  (program.rewards_paid * program.highest_rewards) / 3
              ), // Estimate of paid rewards
            },
            {
              name: "Remaining Rewards",
              type: "line",
              stack: "Total",
              areaStyle: {},
              emphasis: {
                focus: "series",
              },
              data: customerStats.map((program) => program.remaining_rewards),
            },
          ],
        };

      case "radar":
        return {
          ...theme,
          title: {
            text: "Program Performance Metrics",
            left: "center",
            textStyle: titleStyle,
          },
          tooltip: {},
          legend: {
            data: customerStats.map((program) => program.program_title),
            textStyle: { color: "#666666" },
            bottom: 0,
          },
          radar: {
            indicator: [
              { name: "Valid Rate", max: 100 },
              {
                name: "Rewards Paid",
                max: Math.max(...customerStats.map((p) => p.rewards_paid)) + 5,
              },
              {
                name: "Highest Reward",
                max:
                  Math.max(...customerStats.map((p) => p.highest_rewards)) +
                  100,
              },
              {
                name: "Submissions",
                max:
                  Math.max(...customerStats.map((p) => p.total_submissions)) +
                  10,
              },
              {
                name: "Remaining Budget",
                max:
                  Math.max(...customerStats.map((p) => p.remaining_rewards)) +
                  500,
              },
            ],
            radius: "65%",
            axisName: {
              color: "#666666",
              backgroundColor: "#f9f9f9",
              borderRadius: 3,
              padding: [3, 5],
            },
          },
          series: [
            {
              type: "radar",
              data: customerStats.map((program) => {
                const validRate =
                  program.total_submissions > 0
                    ? (program.total_valid_submissions /
                        program.total_submissions) *
                      100
                    : 0;

                return {
                  value: [
                    validRate,
                    program.rewards_paid,
                    program.highest_rewards,
                    program.total_submissions,
                    program.remaining_rewards,
                  ],
                  name: program.program_title,
                  areaStyle: {
                    opacity: 0.3,
                  },
                };
              }),
            },
          ],
        };

      case "heatmap":
        // Generate some derived metrics for the heatmap
        const metrics = customerStats.map((program) => {
          const validRate =
            program.total_submissions > 0
              ? (program.total_valid_submissions / program.total_submissions) *
                100
              : 0;

          const avgReward =
            program.rewards_paid > 0
              ? program.highest_rewards * 0.6 // Simulated average reward
              : 0;

          const rewardEfficiency =
            program.total_valid_submissions > 0
              ? (program.rewards_paid / program.total_valid_submissions) * 100
              : 0;

          return {
            program: program.program_title,
            validRate,
            avgReward,
            rewardEfficiency,
            remainingBudget: program.remaining_rewards,
          };
        });

        // Create data array for heatmap
        const heatmapData = [];
        const metrics_keys = [
          "validRate",
          "avgReward",
          "rewardEfficiency",
          "remainingBudget",
        ];
        const metrics_names = [
          "Validation Rate",
          "Avg Reward",
          "Reward Efficiency",
          "Remaining Budget",
        ];

        metrics.forEach((item, programIndex) => {
          metrics_keys.forEach((key, metricIndex) => {
            heatmapData.push([programIndex, metricIndex, item[key]]);
          });
        });

        return {
          ...theme,
          title: {
            text: "Program Metrics Heatmap",
            left: "center",
            textStyle: titleStyle,
          },
          tooltip: {
            position: "top",
            formatter: function (params) {
              const program = customerStats[params.data[0]].program_title;
              const metric = metrics_names[params.data[1]];
              const value = params.data[2].toFixed(2);

              return `${program}<br>${metric}: ${value}`;
            },
          },
          grid: {
            height: "50%",
            top: "10%",
          },
          xAxis: {
            type: "category",
            data: customerStats.map((program) => program.program_title),
            splitArea: {
              show: true,
            },
            axisLabel: {
              ...axisLabelStyle,
              rotate: 45,
              interval: 0,
            },
          },
          yAxis: {
            type: "category",
            data: metrics_names,
            splitArea: {
              show: true,
            },
            axisLabel: axisLabelStyle,
          },
          visualMap: {
            min: 0,
            max: 1000,
            calculable: true,
            orient: "horizontal",
            left: "center",
            bottom: "0%",
            textStyle: {
              color: "#666666",
            },
            color: ["#7f56d9", "#b38dfc", "#d1b3ff", "#f3e8ff"],
          },
          series: [
            {
              name: "Program Metrics",
              type: "heatmap",
              data: heatmapData,
              label: {
                show: true,
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowColor: "rgba(0, 0, 0, 0.5)",
                },
              },
            },
          ],
        };

      default:
        return {
          title: {
            text: "No chart type selected",
            textStyle: { color: "#333333" },
          },
        };
    }
  }, [type, data]);

  return (
    <div className="w-100" style={{ height: "500px" }}>
      <ReactECharts
        option={chartOption}
        style={{ height: "100%", width: "100%" }}
        className="echarts-for-react"
      />
    </div>
  );
};

export default ChartComponent;
